import { useTranslation } from 'react-i18next';
import Box from 'Components/Atoms/Box';
import Divider from 'Components/Atoms/Divider';
import Typography from 'Components/Atoms/Typography';
import { ReservationActionTypes } from 'Contexts/ReservationContext';
import React, { useEffect, useMemo, useRef } from 'react';
import { Reservation } from 'types/reservations';
import {
  blockToTime,
  noop,
  runsIntoBlock,
  timeHelper,
  timeToBlock,
} from 'utils/helper';
import SmallReservationCard from '../../SmallReservationCard';
import { Close, Lock } from 'Components/Atoms/Icons';
import { ScheduleBand } from 'utils/tableBlocks';
import { TableInfo } from 'CustomHooks/useTableInfo';
import useSharedTablePool from 'CustomHooks/useSharedTablePool';
import { getSharedTableUsage } from 'utils/sharedTableCapacity';

export type TableScheduleProps = {
  start: number;
  close: number;
  shiftWindow?: [number, number] | null;
  reservations: Reservation[];
  currentTime: null | string;
  setHasOverflow: (value: { bottom: boolean; top: boolean }) => void;
  onReservationAction: (
    id: string,
    type: ReservationActionTypes,
    payload?: any
  ) => void;
  handleScrolling?: (
    event: React.UIEvent<HTMLDivElement | HTMLLabelElement | HTMLFormElement>
  ) => void;
  setcurrentReservation: (id: string | null) => void;
  tableInfo: (TableInfo[''][0] & {
    start: number;
    last: number;
    end?: number;
  })[];
  /** True when the sidebar is showing a shared-seating table. */
  shared?: boolean;
  /** Max seats — shown as a capacity header for shared tables. */
  tableMax?: number;
  tableName?: string;
  /** Blocked table and closed space windows drawn on the time axis. */
  bands?: ScheduleBand[];
  /** Given only where lifting a single block from the axis is allowed. */
  onRemoveBlock?: (blockId: string) => void;
};

const timeString = (block: number) => {
  if (block % 4) {
    return ' ';
  } else {
    return blockToTime(block);
  }
};

const TableSchedule: React.FC<TableScheduleProps> = ({
  start,
  close,
  shiftWindow = null,
  reservations,
  currentTime,
  setHasOverflow = noop,
  handleScrolling = noop,
  setcurrentReservation = noop,
  onReservationAction,
  tableInfo,
  shared = false,
  tableMax,
  tableName,
  bands = [],
  onRemoveBlock,
}) => {
  const { t } = useTranslation('reservations');
  const sharedTablePool = useSharedTablePool();

  const blocks = useMemo(() => {
    const startBlock = start;
    const closeBlock = close;
    let blockArray = [];
    for (let i = startBlock; i <= closeBlock; i += 2) {
      blockArray.push(i);
    }
    return blockArray;
  }, [start, close]);

  const currentTimeLine = useRef<null | HTMLDivElement>(null);

  const topCoordinate = (startString: string) => {
    const startResa = timeToBlock(startString);
    return 12 + (startResa - start) * 12;
  };

  useEffect(() => {
    if (currentTimeLine.current) {
      currentTimeLine.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentTimeLine]);

  useEffect(() => {
    const container = document.getElementById('scrollingSchedule');
    if (!!container) {
      const overFlow = container.clientHeight < container.scrollHeight;
      setHasOverflow({ bottom: overFlow, top: false });
    }
  }, [setHasOverflow]);

  const overlaps = useMemo(() => {
    // For shared tables, concurrent reservations are valid — no warning stripes.
    if (shared) return [] as { top: number; height: number }[];

    let overlaps: {
      top: number;
      height: number;
    }[] = [];

    reservations
      // A manual block is not a booking — it may not raise a double-booking
      // stripe, and the schedule doesn't draw it either.
      .filter((r) => !r.blocked)
      .sort((a, b) => a.startTimeInBlocks - b.startTimeInBlocks)
      .forEach((r, i, arr) => {
        if (i > 0) {
          const previousResa = arr[i - 1];
          if (r.startTimeInBlocks < previousResa.endTimeInBlocks) {
            const overflowLength =
              previousResa.endTimeInBlocks - r.startTimeInBlocks;
            overlaps.push({
              top: topCoordinate(r.time),
              height: overflowLength * 12,
            });
          }
        }
      });

    return overlaps;
  }, [reservations, shared]);

  /**
   * Assign each reservation to a "lane" so that overlapping reservations sit
   * in different columns of the sidebar. Classic calendar-event-layout
   * algorithm: pick the lowest-indexed lane whose current occupant ended
   * before this reservation starts.
   *
   * Only relevant for shared tables — for non-shared tables there should be
   * no real overlaps (the warning stripes flag any that exist).
   */
  const lanes = useMemo(() => {
    if (!shared) return null;

    // Blocks never render as cards, so they may not occupy a lane either —
    // an invisible lane would squeeze the visible reservations.
    const sorted = reservations
      .filter((r) => !r.blocked)
      .sort((a, b) => a.startTimeInBlocks - b.startTimeInBlocks);
    const laneEnds: number[] = [];
    const assignments = new Map<string, number>();
    sorted.forEach((r) => {
      let laneIndex = laneEnds.findIndex((end) => end <= r.startTimeInBlocks);
      if (laneIndex === -1) laneIndex = laneEnds.length;
      laneEnds[laneIndex] = r.endTimeInBlocks;
      assignments.set(r.id, laneIndex);
    });
    return { assignments, totalLanes: Math.max(1, laneEnds.length) };
  }, [reservations, shared]);

  /**
   * Seats taken per hour row plus the pool they count against — rendered as
   * "N/max" badges so the host sees at a glance how full a shared table is.
   * A group seated on several shared tables takes its guests out of the
   * combined seats, so its badge reads e.g. 8/12 on both tables instead of
   * filling each one up.
   */
  const capacityByBlock = useMemo(() => {
    if (!shared || !tableMax) return null;

    const map = new Map<number, { used: number; max: number }>();
    for (let b = start; b <= close; b += 4) {
      map.set(
        b,
        getSharedTableUsage(reservations, {
          start: b,
          end: b + 4,
          tableMax,
          tableName,
          pool: sharedTablePool,
        })
      );
    }
    return map;
  }, [
    reservations,
    shared,
    tableMax,
    tableName,
    start,
    close,
    sharedTablePool,
  ]);

  // Same fallbacks as runsIntoBlock, defensive: useReservations fills both.
  const span = (r: Reservation) => {
    const from = r.startTimeInBlocks ?? timeToBlock(r.time);
    return [from, r.endTimeInBlocks ?? from + (r.reservationLength ?? 0)];
  };

  const shown = reservations
    .filter((r) => r.status !== 'failed' && !r.blocked)
    .filter((r) => {
      const [from, till] = span(r);
      // Same rule as the table plan: a reservation from an earlier shift
      // counts while it still runs.
      return shiftWindow
        ? from <= shiftWindow[1] &&
            (from >= shiftWindow[0] ||
              runsIntoBlock(r, shiftWindow[0], currentTime))
        : till > start || from < close;
    });

  // A card pinned to the shift start shares its top with a card starting
  // there, so it paints last; the overlap stripes still paint after all cards.
  const pinned = (r: Reservation) => !!shiftWindow && span(r)[0] < start;
  const ordered = [...shown].sort(
    (a, b) => Number(pinned(a)) - Number(pinned(b))
  );

  return (
    <Box
      id="scrollingSchedule"
      style={{ maxHeight: 'calc(100% - 120px)' }}
      onScroll={(e) => handleScrolling(e)}
    >
      {blocks.map((time) => (
        <Box flex style={{ height: 24 }} key={time}>
          <Typography
            block
            variant="text-4"
            color="subdued"
            translation={null}
            style={{
              width: 42,
              // Hours the axis only shows for a card running past the shift.
              opacity:
                shiftWindow && time > shiftWindow[1] + (shiftWindow[1] % 2)
                  ? 0.4
                  : undefined,
            }}
          >
            {timeString(time)}
          </Typography>
          {currentTime && timeToBlock(currentTime) === time ? (
            <div className="flex fullSize" ref={currentTimeLine}>
              <div className="smallDot"></div>
              <Divider style={{ backgroundColor: 'var(--color-tertiary)' }} />
            </div>
          ) : currentTime && timeToBlock(currentTime) === time + 1 ? (
            <Box>
              <Divider />
              <div
                className="flex fullSize quarter-time-line"
                ref={currentTimeLine}
              >
                <div className="smallDot"></div>
                <Divider style={{ backgroundColor: 'var(--color-tertiary)' }} />
              </div>
            </Box>
          ) : (
            <Divider />
          )}
        </Box>
      ))}
      {tableInfo.map((i, x) => (
        <Box
          className="small-tableInfo-container"
          style={{
            top: 12 + (i.start - start) * 12,
            height: Math.max(
              !!i.open
                ? (timeToBlock(timeHelper()) - i.start) * 12
                : ((i.end ?? i.last) - i.start) * 12,
              20
            ),
            borderRadius: 4,
            backgroundColor: 'var(--color-grey-3)',
            padding: '4px 8px',
          }}
          key={x}
        >
          <span
            style={{
              display: 'inline-block',
              height: 12,
              width: 12,
              borderRadius: 6,
              backgroundColor: i.open
                ? 'var(--color-success)'
                : 'var(--color-secondary)',
            }}
          ></span>
          <small style={{ fontSize: 12, lineHeight: 12 }}>
            {i.balance.toFixed(2).replace('.', ',')} €
          </small>
        </Box>
      ))}
      {bands.map((band) => (
        <Box
          key={band.key}
          className={`schedule-band ${band.kind}${
            band.close - band.start < 3 ? ' compact' : ''
          }`}
          style={{
            top: 12 + (band.start - start) * 12,
            height: (band.close - band.start) * 12,
          }}
        >
          <span className="schedule-band-label">
            <Lock fontSize="inherit" />
            <span>
              {band.label}
              {band.note && (
                <span className="schedule-band-note">{band.note}</span>
              )}
            </span>
          </span>
          {band.blockId && onRemoveBlock && (
            <button
              type="button"
              className="schedule-band-remove"
              title={t('Unblock Table')}
              aria-label={t('Unblock Table')}
              onClick={() => onRemoveBlock(band.blockId as string)}
            >
              <Close fontSize="small" />
            </button>
          )}
        </Box>
      ))}
      {ordered.map((resa) => {
        // For shared tables, place concurrent reservations side-by-side
        // using lane assignments so nothing visually stacks/occludes.
        const laneStyle =
          shared && lanes
            ? (() => {
                const lane = lanes.assignments.get(resa.id) ?? 0;
                const total = lanes.totalLanes;
                return {
                  right: 'auto',
                  left: `calc(45px + ${lane} * (100% - 45px) / ${total})`,
                  width: `calc((100% - 45px) / ${total} - 4px)`,
                } as React.CSSProperties;
              })()
            : {};
        // In a shift view a reservation crossing an edge stays inside the
        // axis: flush with its start, ending at its end. The real start time
        // stays in the card.
        const [from, till] = span(resa);
        const startsBefore = !!shiftWindow && from < start;
        const endsAfter = !!shiftWindow && till > close;
        const cut = startsBefore || endsAfter;
        const top = startsBefore ? start : from;
        // An hour minimum, or its own length when shorter, capped at the next
        // card of the table (lanes keep shared tables apart): a small rest
        // would collapse into a strip.
        const nextStart = shared
          ? Infinity
          : Math.min(
              ...shown
                .filter((r) => r.id !== resa.id && span(r)[0] >= top)
                .map((r) => span(r)[0])
            );
        // The visible part, at least an hour or the own length, capped at the
        // next card (the overlap stripe still marks a double booking) and
        // never under half an hour, so the card stays readable.
        const cardLength = cut
          ? Math.max(
              2,
              Math.min(
                nextStart - top,
                Math.max(Math.min(till, close) - top, Math.min(till - from, 4))
              )
            )
          : undefined;
        return (
          <Box
            onClick={() => setcurrentReservation(resa.id)}
            className="small-resa-container"
            style={{
              top: 12 + (top - start) * 12,
              ...laneStyle,
            }}
            key={resa.id}
          >
            <SmallReservationCard
              reservation={resa}
              handleAction={onReservationAction}
              visibleLength={cardLength}
              continuesBefore={startsBefore}
              continuesAfter={endsAfter}
            />
          </Box>
        );
      })}
      {/*
        Shared-table capacity badges in the gutter: shows how many seats are
        booked per hour vs the table max. Only rendered for shared tables.
      */}
      {shared &&
        capacityByBlock &&
        tableMax &&
        Array.from(capacityByBlock.entries()).map(([block, { used, max }]) => {
          if (used === 0) return null;
          const full = used >= max;
          return (
            <Box
              key={`cap-${block}`}
              style={{
                position: 'absolute',
                top: 12 + (block - start) * 12,
                left: 4,
                width: 38,
                padding: '1px 0',
                textAlign: 'center',
                fontSize: 10,
                fontWeight: 600,
                borderRadius: 4,
                color: '#fff',
                background: full
                  ? 'var(--color-error, #b91c1c)'
                  : 'rgba(0,0,0,0.45)',
                pointerEvents: 'none',
                zIndex: 2,
              }}
              title={`${used} von ${max} Plätzen belegt um ${blockToTime(
                block
              )}`}
            >
              {used}/{max}
            </Box>
          );
        })}
      {overlaps.map((overlap, i) => (
        <Box
          key={'overlap-' + i}
          style={{
            top: overlap.top,
            height: overlap.height,
            backgroundImage: `url("/warning.svg")`,
          }}
          className="overlap"
        ></Box>
      ))}
    </Box>
  );
};

export default TableSchedule;
